import { useMemo } from 'react';

import { ChevronsRightIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { groupBy } from '@/modules/tryIt/utils/helper';
import { NodeStack } from '@/shared/types/tryIt';

type ResponsesWrapperProps = {
  type?: 'node' | 'webhook';
  nodes_stack: NodeStack[];
  flat?: boolean;
  showJumps?: boolean;
} & React.PropsWithChildren<unknown>;

const getJumpToNodes = (array: NodeStack[]) => {
  if (array.length === 0) {
    return [];
  }

  const grouped = groupBy(array, 'request_id');
  const length = grouped.length;

  const lastMessagePath = grouped[length - 1];

  // Exclude the first node
  const jumpToNodes = lastMessagePath.slice(1);

  return jumpToNodes.map((node) => node.name);
};

export const ResponsesWrapper = ({
  type = 'node',
  nodes_stack = [],
  children,
  flat = false,
  showJumps = true,
}: ResponsesWrapperProps) => {
  const options = useMemo(() => {
    if (!showJumps) {
      return [];
    }
    return getJumpToNodes(nodes_stack).map((option) => ({
      label: option,
      startIcon: <ChevronsRightIcon className="text-muted-foreground" size={12} />,
    }));
  }, [nodes_stack, showJumps]);

  if (nodes_stack.length === 0) {
    return children;
  }

  // For backwards compatibility, if the request_id is not present, return children
  if (!nodes_stack?.[0]?.request_id) {
    return children;
  }
  return (
    <div
      className={cn('flex flex-col gap-4', !flat && 'mt-4', {
        'border-t border-dashed border-border': options.length > 0,
        'border border-dashed border-border p-5 pt-0': type === 'webhook',
      })}
    >
      {options.length > 0 && (
        <div className="relative -top-4 left-1/2 my-2 inline-flex -translate-x-1/2 flex-wrap items-center justify-center gap-4 bg-muted/50 px-4">
          {options.map((option, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`${option.label}-${index}`}
              className="flex items-center gap-1 text-sm leading-5 font-normal text-muted-foreground"
            >
              {option.startIcon}
              {option.label}
            </span>
          ))}
        </div>
      )}
      {children}
    </div>
  );
};
